"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const services = [{
  title: "Reentry & Reintegration",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654300/img/reentry_support_pathway.png",
  link: "/ProgramsPage"
}, {
  title: "Mentorship & Development",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654300/img/mentorship_growth_pathway.png",
  link: "/ProgramsPage"
}, {
  title: "Independent Living Readiness",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654300/img/independent_living_pathway.png",
  link: "/ProgramsPage"
}];
export default function OurExpertise() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="235464" id="pathways-to-success" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`mb-12 md:mb-16 max-w-3xl transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-6xl font-bold font-default text-primary mb-4 tracking-tighter">
            Pathways to Success
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default leading-relaxed">
            The Trust Foundation provides comprehensive support systems designed to restore 
            stability, dignity, and opportunity for individuals navigating their second chance.
          </p>
        </div>

        <div className="flex flex-col gap-6 md:gap-8">
          {services.map((service, i) => <ServicePanel key={i} service={service} index={i} />)}
        </div>
      </div>
    </section>;
}
function ServicePanel({
  service,
  index
}: {
  service: {
    title: string;
    image: string;
    link: string;
  };
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  return <div ref={ref} data-index={index} className={`group relative w-full h-[320px] md:h-[400px] lg:h-[480px] rounded-3xl overflow-hidden shadow-xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <img src={service.image} alt={service.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105" />

      {}
      <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent transition-opacity duration-500 group-hover:opacity-95" />

      {}
      <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-10 lg:p-12 backdrop-blur-[2px] group-hover:backdrop-blur-none transition-all duration-500">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-default leading-tight max-w-2xl drop-shadow-lg">
            {service.title}
          </h3>

          <Link to={service.link} className="shrink-0">
            <Button size="lg" className="rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-2xl group/btn font-default font-semibold">
              Explore Program
              <ArrowRight className="ml-2 size-5 transition-transform duration-300 group-hover/btn:translate-x-1" />
            </Button>
          </Link>
        </div>
      </div>
    </div>;
}